import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { useParams } from "react-router-dom";
import ContractorPortalApi from "../../../../../infrastructure/api/contractorPortalApi";
import {
  formatCurrency,
  formatDate,
  formatNumber,
} from "../../../../../infrastructure/utils/formatters";
import ContractorPortalPageShell from "../components/ContractorPortalPageShell";
import ContractorStatusBadge from "../components/ContractorStatusBadge";
import ContractorProjectProgressTab from "../tabs/ContractorProjectProgressTab";
import ContractorProjectDefectsTab from "../tabs/ContractorProjectDefectsTab";
import ContractorProjectPaymentsTab from "../tabs/ContractorProjectPaymentsTab";
import ContractorProjectAcceptanceTab from "../tabs/ContractorProjectAcceptanceTab";

const TAB_IDS = ["overview", "progress", "defects", "payments", "acceptance"];

const ContractorProjectDetailPage = () => {
  const { id } = useParams();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      const res = await ContractorPortalApi.getProjectDetail(id);
      if (cancelled) return;
      if (!res?.isSuccess) {
        setError(res?.message || "خطا در بارگذاری جزئیات پروژه.");
        setDetail(null);
      } else {
        setDetail(res.data);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const project = detail?.project;
  const contract = detail?.contract;
  const annexId = project?.annexId || contract?.annexId;

  return (
    <ContractorPortalPageShell
      title="جزئیات پروژه"
      description="فضای کاری اجرا: پیشرفت، ایرادات، پرداخت‌ها و پذیرش"
      loading={loading}
      error={error}
    >
      {!project ? (
        <Alert severity="info">اطلاعات پروژه در دسترس نیست.</Alert>
      ) : (
        <Stack spacing={2} data-testid="contractor-project-detail">
          <Tabs
            value={tab}
            onChange={(_, value) => setTab(value)}
            variant="scrollable"
            scrollButtons="auto"
            data-testid="project-detail-tabs"
          >
            <Tab label="نمای کلی" data-testid="tab-overview" />
            <Tab label="پیشرفت" data-testid="tab-progress" />
            <Tab label="ایرادات" data-testid="tab-defects" />
            <Tab label="پرداخت‌ها" data-testid="tab-payments" />
            <Tab label="پذیرش" data-testid="tab-acceptance" />
          </Tabs>

          {TAB_IDS[tab] === "overview" ? (
            <Stack spacing={2} data-testid="project-overview-tab">
              <Card variant="outlined" data-testid="project-summary-card">
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                    خلاصه پروژه
                  </Typography>
                  <Stack spacing={1}>
                    <Typography>کد پروژه: {project.projectCode || "—"}</Typography>
                    <Typography>ظرفیت: {project.capacity || "—"}</Typography>
                    <Typography>شهر: {project.city || "—"}</Typography>
                    <Typography>
                      پیشرفت:{" "}
                      {project.progressPercent == null
                        ? "—"
                        : `${formatNumber(project.progressPercent)}٪`}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2">وضعیت اجرا:</Typography>
                      <ContractorStatusBadge
                        status={project.executionStatus}
                        statusTitle={project.executionStatusTitle}
                      />
                    </Stack>
                    {project.startedAt ? (
                      <Typography variant="body2" color="text.secondary">
                        شروع: {formatDate(project.startedAt)}
                      </Typography>
                    ) : null}
                  </Stack>
                </CardContent>
              </Card>

              <Card variant="outlined" data-testid="contract-summary-card">
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                    خلاصه قرارداد
                  </Typography>
                  {!contract ? (
                    <Typography variant="body2" color="text.secondary">
                      پیوست قراردادی مرتبط یافت نشد.
                    </Typography>
                  ) : (
                    <Stack spacing={1}>
                      <Typography>
                        شماره پیوست: {contract.annexNumber || "—"}
                      </Typography>
                      <Typography>وضعیت: {contract.statusTitle || "—"}</Typography>
                      <Typography fontWeight={600}>
                        مبلغ قابل مشاهده پیمانکار:{" "}
                        {contract.contractorContractAmount != null
                          ? formatCurrency(
                              contract.contractorContractAmount,
                              "rial"
                            )
                          : project.contractAmount != null
                            ? formatCurrency(project.contractAmount, "rial")
                            : "—"}
                      </Typography>
                    </Stack>
                  )}
                </CardContent>
              </Card>

              <Card variant="outlined" data-testid="execution-status-card">
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                    وضعیت اجرای فعلی
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <ContractorStatusBadge
                      status={project.executionStatus}
                      statusTitle={project.executionStatusTitle}
                    />
                    {project.hasOpenDefects ? (
                      <Chip size="small" color="warning" label="ایراد باز دارد" />
                    ) : (
                      <Chip
                        size="small"
                        variant="outlined"
                        label="بدون ایراد باز"
                      />
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Stack>
          ) : null}

          {TAB_IDS[tab] === "progress" ? (
            <ContractorProjectProgressTab
              executionId={project.id}
              annexId={annexId}
            />
          ) : null}

          {TAB_IDS[tab] === "defects" ? (
            <ContractorProjectDefectsTab executionId={project.id} />
          ) : null}

          {TAB_IDS[tab] === "payments" ? (
            <ContractorProjectPaymentsTab executionId={project.id} />
          ) : null}

          {TAB_IDS[tab] === "acceptance" ? (
            <ContractorProjectAcceptanceTab executionId={project.id} />
          ) : null}

          <Box sx={{ display: "none" }} data-testid="project-detail-tab-ids">
            {TAB_IDS.join(",")}
          </Box>
        </Stack>
      )}
    </ContractorPortalPageShell>
  );
};

export default ContractorProjectDetailPage;
